import { useEffect, useRef, useState } from 'react';
import { safeFetch } from '../lib/api';

export interface ReferrerJourneyDoc {
  id?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
  rejectionReason?: string;
  name?: string;
  [key: string]: unknown;
}

/**
 * The caller's referrer doc (server truth) for journey UI — the dashboard
 * card and the avatar-menu switcher badge read from this ONE source so
 * they can never disagree about the application state.
 *
 * - `doc`: the referrer doc, or null when the server definitively has
 *   none (404).
 * - `fetchFailed`: the lookup errored (NOT a 404) — state is unknown and
 *   callers must fail closed.
 * - `loading`: request in flight.
 */
export function useReferrerJourney() {
  const [doc, setDoc] = useState<ReferrerJourneyDoc | null>(null);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    const check = async () => {
      try {
        const data = (await safeFetch('/api/referrer/profile')) as { referrer?: ReferrerJourneyDoc };
        if (cancelledRef.current) return;
        setDoc(data?.referrer ?? null);
        setFetchFailed(false);
      } catch (err: any) {
        if (cancelledRef.current) return;
        if (err?.status === 404) {
          setDoc(null);
          setFetchFailed(false);
        } else {
          setFetchFailed(true);
        }
      } finally {
        if (!cancelledRef.current) setLoading(false);
      }
    };
    check();
    const onFocus = () => { check(); };
    window.addEventListener('focus', onFocus);
    return () => {
      cancelledRef.current = true;
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  return { doc, fetchFailed, loading };
}

export default useReferrerJourney;
